import { VariantLabel } from '../components/VariantLabel';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, LogIn, MapPin, Package, Truck, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { cartAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { trackViewCart, trackRemoveFromCart } from '../utils/analytics';
import api from '../lib/api';
import LockerPickerDialog from '../components/LockerPickerDialog';
import { GoogleLoginButton } from '../components/GoogleLoginButton';
import { optimizedImage } from '../utils/productImage';

const Cart = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { 
    refreshCounts, 
    getGuestCart, 
    updateGuestCartItem, 
    removeFromGuestCart,
    isAuthenticated 
  } = useCart();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Easybox state for each supplier
  const [supplierEasybox, setSupplierEasybox] = useState({}); // { supplierId: locker }
  const [lockerDialogOpen, setLockerDialogOpen] = useState(false);
  const [currentSupplierForLocker, setCurrentSupplierForLocker] = useState(null);
  const [lockers, setLockers] = useState([]);
  const [loadingLockers, setLoadingLockers] = useState(false);
  
  // Suppliers with easybox integration
  const [suppliersWithEasybox, setSuppliersWithEasybox] = useState([]);
  const [easyboxPrices, setEasyboxPrices] = useState({}); // { supplierId: { courier_price, easybox_price, savings } }
  const [supplierTransport, setSupplierTransport] = useState({}); // { supplierId: { cost, easyboxCost, freeThreshold, label } }

  // Load transport pricing for a supplier (default provider)
  const loadSupplierTransport = async (supplierId) => {
    try {
      const res = await api.get(`/transport/provider/${supplierId}`);
      setSupplierTransport(prev => ({ ...prev, [supplierId]: res.data }));
    } catch (error) {
      console.error('Error loading transport pricing:', error);
    }
  };

  // Load suppliers with easybox integration
  useEffect(() => {
    const loadSuppliersWithEasybox = async () => {
      try {
        const response = await api.get('/sameday/suppliers-with-easybox');
        setSuppliersWithEasybox(response.data?.suppliers || []);
      } catch (error) {
        console.error('Error loading suppliers with easybox:', error);
      }
    };
    loadSuppliersWithEasybox();
  }, []);

  // Load lockers
  const loadLockers = async () => {
    setLoadingLockers(true);
    try {
      const response = await api.get('/sameday/lockers');
      setLockers(response.data || []);
    } catch (error) {
      console.error('Error loading lockers:', error);
    } finally {
      setLoadingLockers(false);
    }
  };

  // Load easybox price for a supplier
  const loadEasyboxPrice = async (supplierId, subtotal) => {
    try {
      const response = await api.post('/sameday/easybox-price', {
        supplier_id: supplierId,
        subtotal: subtotal,
        total_weight: 1.0
      });
      setEasyboxPrices(prev => ({
        ...prev,
        [supplierId]: response.data
      }));
    } catch (error) {
      console.error('Error loading easybox price:', error);
    }
  };

  useEffect(() => {
    loadCart();
  }, [user]);

  const loadCart = async () => {
    setLoading(true);
    try {
      let items = [];
      if (isAuthenticated()) {
        // Logged in user - load from API
        const response = await cartAPI.get();
        items = response.data.items || [];
      } else {
        // Guest user - load from localStorage
        items = getGuestCart();
      }
      setCartItems(items);
      
      // Track view_cart event
      if (items.length > 0) {
        trackViewCart(items);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
      // Fallback to guest cart on error
      const guestCart = getGuestCart();
      setCartItems(guestCart);
    } finally {
      setLoading(false);
    }
  };

  const sameLine = (item, productId, stockId) =>
    item.productId === productId && (item.stockId || null) === (stockId || null);

  const updateQuantity = async (productId, newQuantity, stockId) => {
    if (newQuantity < 1) return;
    
    try {
      if (isAuthenticated()) {
        await cartAPI.updateItem(productId, { quantity: newQuantity }, stockId);
      } else {
        updateGuestCartItem(productId, newQuantity, stockId);
      }
      
      // Update local state
      setCartItems(items =>
        items.map(item => sameLine(item, productId, stockId) ? { ...item, quantity: newQuantity } : item)
      );
      refreshCounts();
      toast({
        title: 'Cantitate actualizată!',
        description: 'Cantitatea produsului a fost actualizată.',
      });
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast({
        title: 'Eroare!',
        description: 'Nu s-a putut actualiza cantitatea.',
        variant: 'destructive',
      });
    }
  };

  const removeItem = async (productId, stockId) => {
    try {
      // Find the item before removing for tracking
      const itemToRemove = cartItems.find(item => sameLine(item, productId, stockId));
      
      if (isAuthenticated()) {
        await cartAPI.removeItem(productId, stockId);
      } else {
        removeFromGuestCart(productId, stockId);
      }
      
      // Track remove_from_cart event
      if (itemToRemove) {
        trackRemoveFromCart(itemToRemove, itemToRemove.quantity);
      }
      
      setCartItems(items => items.filter(item => !sameLine(item, productId, stockId)));
      refreshCounts();
      toast({
        title: 'Produs eliminat!',
        description: 'Produsul a fost eliminat din coș.',
      });
    } catch (error) {
      console.error('Error removing item:', error);
      toast({
        title: 'Eroare!',
        description: 'Nu s-a putut elimina produsul.',
        variant: 'destructive',
      });
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  
  // Default shipping costs (fallback until provider pricing loads)
  const DEFAULT_EASYBOX_SHIPPING = 15;
  const DEFAULT_COURIER_SHIPPING = 25;
  const FREE_THRESHOLD = 400;
  
  // Group cart items by supplier
  const itemsBySupplier = cartItems.reduce((acc, item) => {
    const supplierId = item.supplierId || 'direct';
    if (!acc[supplierId]) {
      acc[supplierId] = {
        supplierName: item.supplierName || (supplierId === 'direct' ? 'R32 Direct' : 'Furnizor'),
        items: [],
        subtotal: 0
      };
    }
    acc[supplierId].items.push(item);
    acc[supplierId].subtotal += item.price * item.quantity;
    return acc;
  }, {});
  
  // Check if supplier has easybox integration
  const supplierHasEasybox = (supplierId) => {
    return suppliersWithEasybox.some(s => s.id === supplierId);
  };
  
  // Calculate shipping for each supplier group (with easybox option)
  const supplierGroups = Object.entries(itemsBySupplier).map(([supplierId, group]) => {
    const hasEasyboxIntegration = supplierHasEasybox(supplierId);
    const hasSelectedEasybox = supplierEasybox[supplierId];

    // Provider pricing for this supplier's default transport provider
    const transport = supplierTransport[supplierId];
    const baseCourier = transport?.cost ?? DEFAULT_COURIER_SHIPPING;
    const baseEasybox = transport?.easyboxCost ?? DEFAULT_EASYBOX_SHIPPING;
    const threshold = transport?.freeThreshold ?? FREE_THRESHOLD;
    const freeShipping = group.subtotal >= threshold;

    const courierShipping = freeShipping ? 0 : baseCourier;
    const easyboxShipping = freeShipping ? 0 : baseEasybox;
    const savings = courierShipping - easyboxShipping;
    const minOrderValue = transport?.minOrderValue ?? 0;
    const belowMinOrder = minOrderValue > 0 && group.subtotal < minOrderValue;
    // Produse voluminoase (ex. panouri, invertoare 7SUN): transportul se stabilește la telefon
    const shippingOnRequest = group.items.some((i) => i.shippingOnRequest || i.product?.shippingOnRequest);

    return {
      supplierId,
      supplierName: group.supplierName, // Use actual supplier name
      items: group.items,
      subtotal: group.subtotal,
      minOrderValue,
      belowMinOrder,
      minOrderMissing: belowMinOrder ? minOrderValue - group.subtotal : 0,
      shippingOnRequest,
      shipping: shippingOnRequest ? 0 : (hasSelectedEasybox ? easyboxShipping : courierShipping),
      courierShipping,
      easyboxShipping,
      threshold,
      savings,
      freeShipping,
      hasEasyboxIntegration, // Supplier supports easybox
      hasSelectedEasybox: !!hasSelectedEasybox, // User chose easybox
      selectedLocker: hasSelectedEasybox || null,
      canSaveWithEasybox: hasEasyboxIntegration && !freeShipping && !hasSelectedEasybox && savings > 0
    };
  });
  
  // Load transport pricing for each supplier in the cart
  const supplierIds = supplierGroups.map(g => g.supplierId).join(',');
  useEffect(() => {
    supplierGroups.forEach(group => {
      if (group.supplierId && group.supplierId !== 'direct' && !supplierTransport[group.supplierId]) {
        loadSupplierTransport(group.supplierId);
      }
    });
  }, [supplierIds]); // Only re-run when supplier list changes
  
  // Calculate total shipping (sum of all supplier shipping costs)
  const totalShipping = supplierGroups.reduce((sum, group) => sum + group.shipping, 0);
  const shippingOnRequest = supplierGroups.some((g) => g.shippingOnRequest);
  const total = subtotal + totalShipping;
  const belowMinGroups = supplierGroups.filter((g) => g.belowMinOrder);
  
  // Handle easybox selection for a supplier
  const handleSelectEasybox = (supplierId) => {
    setCurrentSupplierForLocker(supplierId);
    loadLockers();
    setLockerDialogOpen(true);
  };
  
  // Handle locker selection
  const handleLockerSelect = (locker) => {
    if (currentSupplierForLocker) {
      setSupplierEasybox(prev => ({
        ...prev,
        [currentSupplierForLocker]: locker
      }));
    }
    setLockerDialogOpen(false);
    setCurrentSupplierForLocker(null);
  };
  
  // Remove easybox selection for a supplier
  const handleRemoveEasybox = (supplierId) => {
    setSupplierEasybox(prev => {
      const newState = { ...prev };
      delete newState[supplierId];
      return newState;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Coșul Meu</h1>

        {cartItems.length === 0 ? (
          <Card className="p-12 text-center rounded-2xl">
            <ShoppingBag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-4">Coșul tău este gol</p>
            <Link to="/catalog">
              <Button className="bg-green-600 hover:bg-green-700 rounded-xl">
                Începe cumpărăturile
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items - Grouped by Supplier */}
            <div className="lg:col-span-2 space-y-6">
              {supplierGroups.map((group) => (
                <div key={group.supplierId} className="space-y-4">
                  {/* Supplier Header */}
                  <div className="flex items-center justify-between bg-gray-100 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Package className="h-5 w-5 text-gray-600" />
                      <span className="text-sm font-semibold text-gray-700">
                        Comandă {group.supplierName}
                      </span>
                      <span className="text-xs text-gray-500">
                        ({group.items.length} {group.items.length === 1 ? 'produs' : 'produse'})
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{group.subtotal.toFixed(2)} Lei</p>
                      <p className="text-xs text-gray-500">
                        {group.shippingOnRequest ? (
                          <span className="text-amber-700" data-testid={`shipping-on-request-${group.supplierId}`}>
                            Transport: va fi comunicat la confirmarea telefonică a comenzii
                          </span>
                        ) : (
                          <>
                            + {group.shipping === 0 ? (
                              <span className="text-green-600">Livrare gratuită</span>
                            ) : (
                              <span className={group.hasSelectedEasybox ? 'text-green-600' : ''}>
                                {group.shipping} Lei {group.hasSelectedEasybox ? '(easybox)' : 'livrare'}
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  {group.belowMinOrder && (
                    <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid={`min-order-warning-${group.supplierId}`}>
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>
                        Comanda minimă la <b>{group.supplierName}</b> este <b>{group.minOrderValue.toFixed(0)} lei</b>. Ai {group.subtotal.toFixed(2)} lei — mai adaugă produse de <b>{group.minOrderMissing.toFixed(2)} lei</b> de la acest furnizor.
                      </span>
                    </div>
                  )}
                  
                  {/* Easybox savings suggestion - ONLY for suppliers with Sameday integration */}
                  {group.canSaveWithEasybox && (
                    <Card className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-blue-800">
                              💡 Poți economisi {group.savings} Lei!
                            </p>
                            <p className="text-xs text-blue-600">
                              Alege livrare la easybox Sameday în loc de curier ({group.easyboxShipping} Lei în loc de {group.courierShipping} Lei)
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleSelectEasybox(group.supplierId)}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                        >
                          Alege Easybox
                        </Button>
                      </div>
                    </Card>
                  )}
                  
                  {/* Selected easybox for this supplier */}
                  {/* Selected easybox for this supplier */}
                  {group.hasSelectedEasybox && group.selectedLocker && (
                    <Card className="p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-green-800">
                              ✓ Livrare la Easybox - Economisești {group.savings} Lei!
                            </p>
                            <p className="text-xs text-green-700 font-medium">{group.selectedLocker.name}</p>
                            <p className="text-xs text-green-600">{group.selectedLocker.address}, {group.selectedLocker.city}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelectEasybox(group.supplierId)}
                            className="text-xs border-green-300 text-green-700"
                          >
                            Schimbă
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRemoveEasybox(group.supplierId)}
                            className="text-xs text-red-600 hover:bg-red-50"
                          >
                            Anulează
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}
                  
                  {/* Products from this supplier */}
                  {group.items.map(item => (
                    <Card key={`${item.productId}-${item.stockId || ""}`} className="rounded-2xl border-2 border-gray-100 p-3 sm:p-6">
                      <div className="flex gap-3 sm:items-center sm:gap-4">
                        <Link to={`/product/${item.slug || item.productId}`} className="shrink-0">
                          <img
                            src={optimizedImage(item.image, 200)}
                            alt={item.name}
                            loading="lazy"
                            className="h-20 w-20 cursor-pointer rounded-xl bg-white object-contain transition-opacity hover:opacity-80 sm:h-24 sm:w-24"
                            onError={(e) => { e.target.onerror = null; e.target.src = '/placeholder-product.svg'; }}
                          />
                        </Link>
                        <div className="min-w-0 flex-1">
                          <Link to={`/product/${item.slug || item.productId}`}>
                            <h3 className="mb-1 line-clamp-2 text-sm font-semibold leading-snug transition-colors hover:text-green-600 sm:text-lg">
                              {item.name}
                            </h3>
                          </Link>
                          {item.brand && (
                            <p className="mb-1 text-xs text-gray-500">{item.brand}</p>
                          )}
                          <VariantLabel item={item} className="mb-1 inline-block rounded-md bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-800"
                            testId={`cart-item-variant-${item.productId}-${item.stockId || ''}`} />
                          <p className="text-xl font-bold text-green-600 sm:text-2xl">{item.price} Lei</p>

                          {/* Mobile: cantitate + ștergere sub produs, ținte de atingere mari */}
                          <div className="mt-2 flex items-center justify-between gap-3 sm:hidden">
                            <div className="flex items-center rounded-xl border-2 border-gray-200">
                              <button
                                onClick={() => updateQuantity(item.productId, item.quantity - 1, item.stockId)}
                                aria-label="Scade cantitatea"
                                className="flex h-11 w-11 items-center justify-center rounded-l-xl active:bg-gray-100"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                              <span className="w-8 text-center font-semibold">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.productId, item.quantity + 1, item.stockId)}
                                aria-label="Crește cantitatea"
                                className="flex h-11 w-11 items-center justify-center rounded-r-xl active:bg-gray-100"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                            </div>
                            <button
                              onClick={() => removeItem(item.productId, item.stockId)}
                              aria-label="Șterge produsul"
                              className="flex h-11 w-11 items-center justify-center rounded-xl text-red-500 active:bg-red-50"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </div>

                        <div className="hidden items-center space-x-4 sm:flex">
                          <div className="flex items-center border-2 border-gray-200 rounded-xl">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateQuantity(item.productId, item.quantity - 1, item.stockId)}
                              className="rounded-l-xl"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <span className="px-4 font-semibold">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => updateQuantity(item.productId, item.quantity + 1, item.stockId)}
                              className="rounded-r-xl"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.productId, item.stockId)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
                          >
                            <Trash2 className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                  {/* Free shipping progress for this supplier */}
                  {group.shippingOnRequest ? (
                    <div className="flex items-center gap-2 pl-4" data-testid={`shipping-on-request-note-${group.supplierId}`}>
                      <Truck className="h-4 w-4 text-amber-600" />
                      <p className="text-sm font-medium text-amber-700">
                        Transportul va fi comunicat la confirmarea telefonică a comenzii
                      </p>
                    </div>
                  ) : group.freeShipping ? (
                    <div className="flex items-center gap-2 pl-4" data-testid={`free-shipping-unlocked-${group.supplierId}`}>
                      <Truck className="h-4 w-4 text-green-600" />
                      <p className="text-sm text-green-600 font-medium">
                        🎉 Ai livrare gratuită de la {group.supplierName}!
                      </p>
                    </div>
                  ) : (
                    <div className="px-4" data-testid={`free-shipping-progress-${group.supplierId}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-sm text-gray-600">
                          Mai adaugă <span className="font-semibold text-green-700">{(group.threshold - group.subtotal).toFixed(2)} Lei</span> pentru livrare gratuită
                        </p>
                        <span className="text-xs text-gray-400">{group.subtotal.toFixed(0)}/{group.threshold} Lei</span>
                      </div>
                      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, (group.subtotal / group.threshold) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div>
              <Card className="p-6 rounded-2xl border-2 border-gray-100 sticky top-24">
                <h2 className="text-xl font-bold mb-6">Sumar Comandă</h2>

                {/* Summary per supplier */}
                {supplierGroups.length > 1 && (
                  <div className="space-y-3 mb-4 pb-4 border-b">
                    <p className="text-sm text-gray-600 font-medium">
                      ⚠️ Comanda ta va fi împărțită în {supplierGroups.length} comenzi separate:
                    </p>
                    {supplierGroups.map((group) => (
                      <div key={group.supplierId} className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {group.supplierName}
                          {group.hasSelectedEasybox && <span className="text-green-600 ml-1">(easybox)</span>}:
                        </span>
                        <span>
                          {group.subtotal.toFixed(2)} + {group.shipping === 0 ? '0' : group.shipping} Lei
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal produse:</span>
                    <span className="font-semibold">{subtotal.toFixed(2)} Lei</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Livrare totală:</span>
                    <span className="font-semibold">
                      {shippingOnRequest ? (
                        <span className="text-amber-700">Se comunică la telefon</span>
                      ) : totalShipping === 0 ? (
                        <span className="text-green-600">Gratuit</span>
                      ) : (
                        `${totalShipping.toFixed(2)} Lei`
                      )}
                    </span>
                  </div>
                  {shippingOnRequest && (
                    <p className="text-xs text-amber-700" data-testid="cart-shipping-on-request-note">
                      Comanda conține produse voluminoase: transportul va fi comunicat la confirmarea
                      telefonică a comenzii.
                    </p>
                  )}
                  {supplierGroups.length > 1 && totalShipping > 0 && (
                    <p className="text-xs text-gray-500">
                      (livrare separată pentru fiecare furnizor)
                    </p>
                  )}
                  <div className="border-t pt-4">
                    <div className="flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-green-600">{total.toFixed(2)} Lei</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Input
                    type="text"
                    placeholder="Cod promoțional"
                    className="rounded-xl border-2"
                  />
                  <Button variant="outline" className="w-full rounded-xl border-2">
                    Aplică Cod
                  </Button>
                </div>

                {/* Checkout Button - works for both guest and logged in users; blocked while a supplier is under its minimum order */}
                {belowMinGroups.length > 0 ? (
                  <>
                    <Button
                      disabled
                      className="w-full mt-6 bg-gray-300 text-gray-600 rounded-xl py-6 text-lg font-semibold cursor-not-allowed"
                      data-testid="checkout-button-blocked"
                    >
                      Finalizează Comanda
                    </Button>
                    <p className="mt-2 text-xs text-amber-700 text-center" data-testid="checkout-blocked-reason">
                      Comanda minimă nu este atinsă la: {belowMinGroups.map((g) => `${g.supplierName} (mai adaugă ${g.minOrderMissing.toFixed(2)} lei)`).join(', ')}.
                    </p>
                  </>
                ) : (
                <Link to="/checkout" state={{ 
                  cartItems, 
                  total, 
                  subtotal, 
                  shipping: totalShipping, 
                  supplierGroups,
                  supplierEasybox // Pass easybox selections to checkout
                }}>
                  <Button 
                    className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white rounded-xl py-6 text-lg font-semibold"
                    data-testid="checkout-button"
                  >
                    Finalizează Comanda
                  </Button>
                </Link>
                )}

                {/* Google sign-in for guest users */}
                {!isAuthenticated() && (
                  <div className="mt-4 rounded-xl border-2 border-gray-100 bg-gray-50 p-4" data-testid="cart-guest-auth">
                    <p className="text-sm font-semibold text-gray-900">Comandă mai rapid</p>
                    <p className="mb-3 text-xs text-gray-600">
                      Intră cu Google și îți completăm singuri datele de livrare.
                    </p>
                    <GoogleLoginButton label="Continuă cu Google" returnTo="/checkout" testid="cart-google-login-btn" />
                    <Link to="/login" state={{ from: '/cart' }} className="mt-3 block text-center text-xs font-medium text-gray-500 hover:text-gray-700">
                      Sau autentifică-te cu email și parolă
                    </Link>
                  </div>
                )}

                <Link to="/catalog">
                  <Button variant="outline" className="w-full mt-3 rounded-xl border-2">
                    Continuă Cumpărăturile
                  </Button>
                </Link>
              </Card>
            </div>
          </div>
        )}
      </div>
      
      {/* Mobile: bară fixă cu total + finalizare (deasupra navigației de jos) */}
      {cartItems.length > 0 && (
        <div className="mobile-sticky-bar fixed bottom-14 left-0 right-0 z-30 border-t border-gray-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur lg:hidden" data-testid="cart-mobile-bar">
          <div className="flex items-center gap-3">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Total</p>
              <p className="text-lg font-bold text-green-600" data-testid="cart-mobile-total">{total.toFixed(2)} Lei</p>
            </div>
            {belowMinGroups.length > 0 ? (
              <Button disabled className="h-12 flex-1 cursor-not-allowed rounded-xl bg-gray-300 text-sm font-semibold text-gray-600" data-testid="cart-mobile-checkout-blocked">
                Comandă minimă neatinsă
              </Button>
            ) : (
              <Link
                to="/checkout"
                state={{ cartItems, total, subtotal, shipping: totalShipping, supplierGroups, supplierEasybox }}
                className="flex-1"
              >
                <Button className="h-12 w-full rounded-xl bg-green-600 text-sm font-semibold hover:bg-green-700" data-testid="cart-mobile-checkout">
                  Finalizează comanda
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Locker Picker Dialog */}
      <LockerPickerDialog
        open={lockerDialogOpen}
        onOpenChange={setLockerDialogOpen}
        lockers={lockers}
        loading={loadingLockers}
        onSelect={handleLockerSelect}
      />
    </div>
  );
};

export default Cart;
